import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { ADMIN_ROLE_ID, HOUSEKEEPING_ROLE_ID, FRONT_DESK_ROLE_ID } from '@/lib/roles';

interface Profile {
  id: string;
  full_name: string | null;
  is_active: boolean;
  role_id: string;
  access_level: 'super_admin' | 'operations_manager' | 'staff';
  department_ids: string[];
  permissions: string[];
  default_landing_page: string | null;
}

interface AuthState {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  authError: string | null;
}

interface AuthContextValue extends AuthState {
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  const loadProfile = useCallback(async (userId: string) => {
    try {
      const { data: prof, error } = await supabase
        .from('profiles')
        .select('id, full_name, is_active, role_id')
        .eq('id', userId)
        .maybeSingle();

      if (error || !prof) {
        setAuthError('Profile not found');
        await supabase.auth.signOut();
        setProfile(null);
        setSession(null);
        setUser(null);
        return;
      }

      if (!prof.is_active) {
        setAuthError('Account is disabled');
        await supabase.auth.signOut();
        setProfile(null);
        setSession(null);
        setUser(null);
        return;
      }

      if (prof.role_id !== ADMIN_ROLE_ID && prof.role_id !== HOUSEKEEPING_ROLE_ID && prof.role_id !== FRONT_DESK_ROLE_ID) {
        setAuthError('Invalid role');
        await supabase.auth.signOut();
        setProfile(null);
        setSession(null);
        setUser(null);
        return;
      }

      const { data: accessData } = await supabase.rpc('get_my_staff_access');
      const access = accessData as { access_level?: Profile['access_level']; department_ids?: string[]; permissions?: string[]; default_landing_page?: string | null } | null;
      setProfile({
        ...prof,
        access_level: access?.access_level || (prof.role_id === ADMIN_ROLE_ID ? 'super_admin' : 'staff'),
        department_ids: access?.department_ids || [],
        permissions: access?.permissions || [],
        default_landing_page: access?.default_landing_page || null,
      });
      setAuthError(null);
    } catch {
      setAuthError('Authentication error');
      await supabase.auth.signOut();
      setProfile(null);
      setSession(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      if (cancelled) return;
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      if (initialSession?.user) {
        loadProfile(initialSession.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, currentSession) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);

      if (currentSession?.user) {
        setTimeout(() => {
          if (!cancelled) {
            loadProfile(currentSession.user.id);
          }
        }, 0);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [loadProfile]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const value: AuthContextValue = {
    session,
    user,
    profile,
    loading,
    authError,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
